import { memo, useCallback, type FC } from "react";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { EmailLibraryTemplate } from "src/services/CampaignAPI";
import { TEMPLATE_EDITOR_KEY } from "../TemplateEditorConstants";

const CARD_KEY = `${TEMPLATE_EDITOR_KEY}.card`;

interface TemplateCardProps {
  template: EmailLibraryTemplate;
  brandName: string;
  isApplying: boolean;
  isDisabled: boolean;
  onPreview: (_templateId: string) => void;
  onUse: (_templateId: string) => void;
}

const TemplateCard: FC<TemplateCardProps> = ({
  template,
  brandName,
  isApplying,
  isDisabled,
  onPreview,
  onUse,
}) => {
  const { t } = useTranslation();
  const handlePreview = useCallback(() => onPreview(template.id), [onPreview, template.id]);
  const handleUse = useCallback(() => onUse(template.id), [onUse, template.id]);

  return (
    <article className="template-card">
      <header className="template-card__header">
        <h4 className="template-card__title">{template.name}</h4>
      </header>

      <div className="template-card__preview" aria-hidden>
        <div className="template-card__brand">
          <span className="template-card__avatar">{brandName.charAt(0).toUpperCase()}</span>
          <span className="template-card__brand-name">{brandName}</span>
        </div>
        {template.campaign_subtype && (
          <span className="template-card__eyebrow">{template.campaign_subtype}</span>
        )}
        <p className="template-card__headline">{template.preview_headline}</p>
        <div className="template-card__media">
          {template.preview_image_url && (
            <img
              src={template.preview_image_url}
              alt=""
              loading="lazy"
              className="template-card__image"
            />
          )}
          {template.badge && <span className="template-card__media-tag">{template.badge}</span>}
        </div>
      </div>

      <div className="template-card__actions">
        <button
          type="button"
          className="template-card__action template-card__action--secondary"
          onClick={handlePreview}
          disabled={isDisabled}
          aria-label={t(`${CARD_KEY}.previewLabel`, { name: template.name })}
        >
          {t(`${CARD_KEY}.preview`)}
        </button>
        <button
          type="button"
          className="button button--primary template-card__action"
          onClick={handleUse}
          disabled={isDisabled}
          aria-label={t(`${CARD_KEY}.useLabel`, { name: template.name })}
        >
          {isApplying ? t(`${CARD_KEY}.applying`) : t(`${CARD_KEY}.useTemplate`)}
        </button>
      </div>
    </article>
  );
};

export default memo(TemplateCard);

interface CreateOwnTemplateCardProps {
  isApplying: boolean;
  isDisabled: boolean;
  onCreate: () => void;
}

const CreateOwnTemplateCardBase: FC<CreateOwnTemplateCardProps> = ({
  isApplying,
  isDisabled,
  onCreate,
}) => {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      className="template-create-card"
      onClick={onCreate}
      disabled={isDisabled}
    >
      <span className="template-create-card__icon" aria-hidden>
        <Plus size={16} strokeWidth={2.5} />
      </span>
      <span className="template-create-card__label">
        {isApplying
          ? t(`${TEMPLATE_EDITOR_KEY}.card.applying`)
          : t(`${TEMPLATE_EDITOR_KEY}.createOwn`)}
      </span>
    </button>
  );
};

export const CreateOwnTemplateCard = memo(CreateOwnTemplateCardBase);
